import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useToken from 'hooks/useToken';
import useVacationLetterPDF from 'hooks/useVacationLetterPDF';
import useOrdenNotificaciones from '../cliente/orden/hooks/useOrdenNotificaciones';
import PropTypes from 'prop-types';
import React, { useState, useMemo, useEffect } from 'react';
import { Badge, Button, Col, Form, Modal, Row, Spinner, ListGroup } from 'react-bootstrap';
import { Link } from 'react-router-dom';

const parseSafeDate = (dateVal) => {
    if (!dateVal) return new Date();
    if (dateVal instanceof Date) return dateVal;
    if (typeof dateVal === 'string') {
        const cleanStr = dateVal.split('T')[0]; // gets "YYYY-MM-DD"
        const parts = cleanStr.split('-');
        if (parts.length === 3) {
            const year = Number.parseInt(parts[0], 10);
            const month = Number.parseInt(parts[1], 10);
            const day = Number.parseInt(parts[2], 10);
            if (!Number.isNaN(year) && !Number.isNaN(month) && !Number.isNaN(day)) {
                return new Date(year, month - 1, day);
            }
        }
    }
    return new Date(dateVal);
};

const getValidAvatar = (imgStr) => {
    if (!imgStr || typeof imgStr !== 'string' || imgStr === 'data:image/jpg;base64,' || imgStr === 'data:image/jpeg;base64,' || imgStr.length <= 100) {
        return '/img/team/nofoto50.png';
    }
    return imgStr;
};

const renderCategoriaBadge = (catVal) => {
    switch (Number(catVal)) {
        case 1:
            return <Badge bg="soft-primary" className="text-primary fs--1">Empleado</Badge>;
        case 2:
            return <Badge bg="soft-danger" className="text-danger fs--1">Hon. Prof.</Badge>;
        case 3:
            return <Badge bg="soft-warning" className="text-warning fs--1">Residente</Badge>;
        case 4:
            return <Badge bg="soft-success" className="text-success fs--1">Becario</Badge>;
        default:
            return null;
    }
};

const VacationDetailsModal = ({
    show,
    onHide,
    employee,
    onSuccess,
    showActions = true
}) => {
    const { token, idCatTipoUsuario } = useToken();
    const { generatePDF, isGenerating } = useVacationLetterPDF();
    const { sendPushNotificationRealTime } = useOrdenNotificaciones();
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [motivoRechazo, setMotivoRechazo] = useState('');
    const [procesando, setProcesando] = useState(false);
    const [imagenPerfil, setImagenPerfil] = useState('');
    const [cargandoImagen, setCargandoImagen] = useState(false);

    console.log("Datos completos de employee:", employee);

    useEffect(() => {
        const cargarImagenPerfil = async () => {
            if (!employee?.idUsuario) {
                setImagenPerfil('');
                return;
            }
            setCargandoImagen(true);
            try {
                const response = await fetch(`/api/perfil/obtenerImagenPerfil/?idUsuario=${employee.idUsuario}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': 'Bearer ' + token,
                        'Content-Type': 'text/plain'
                    }
                });
                if (response.ok) {
                    const data = await response.text();
                    setImagenPerfil(data);
                } else {
                    setImagenPerfil('');
                }
            } catch (error) {
                setImagenPerfil('');
            } finally {
                setCargandoImagen(false);
            }
        };

        if (show && employee) {
            cargarImagenPerfil();
        }
    }, [employee?.idUsuario, show, token]);

    const fechasEspecificas = useMemo(() => {
        if (!employee) return null;
        const rawFechas = employee.vFechas || employee.dFechaVacaciones || employee.diasDesglosados || employee.vFechasJson;
        if (!rawFechas) return null;
        try {
            let fechas = [];
            if (typeof rawFechas === 'string') {
                fechas = JSON.parse(rawFechas);
            } else if (Array.isArray(rawFechas)) {
                fechas = rawFechas;
            }
            if (!Array.isArray(fechas) || fechas.length === 0) return null;
            return fechas.map(fechaStr => {
                const fecha = parseSafeDate(fechaStr);
                return {
                    dateString: fechaStr,
                    formatted: fecha.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }),
                    isWeekend: fecha.getDay() === 0 || fecha.getDay() === 6
                };
            });
        } catch (error) {
            return null;
        }
    }, [employee?.vFechas, employee?.dFechaVacaciones, employee?.diasDesglosados, employee?.vFechasJson]);

    if (!employee) return null;

    const getStatusBadge = (estado) => {
        const estados = {
            'PENDIENTE': { bg: 'warning', text: 'Pendiente' },
            'APROBADA': { bg: 'success', text: 'Aprobada' },
            'RECHAZADA': { bg: 'danger', text: 'Rechazada' }
        };
        const status = estados[estado?.toUpperCase()] || { bg: 'secondary', text: estado };
        return <Badge bg={status.bg}>{status.text}</Badge>;
    };

    const formatDate = (dateValue) => {
        if (!dateValue) return 'Fecha no disponible';
        try {
            // Si ya es un objeto Date válido
            if (dateValue instanceof Date && !isNaN(dateValue)) {
                return dateValue.toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
            }

            // Si viene como string, verificamos si es solo fecha (YYYY-MM-DD) para evitar el desfase UTC
            let date;
            if (typeof dateValue === 'string' && dateValue.includes('-') && !dateValue.includes('T')) {
                // Forzamos que se interprete en hora local agregando la hora de manera explícita
                date = new Date(dateValue + 'T00:00:00');
            } else {
                date = new Date(dateValue);
            }

            if (!isNaN(date)) {
                return date.toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
            }
            return 'Fecha no válida';
        } catch (e) {
            return 'Error en fecha';
        }
    };

    const handleGeneratePDF = () => {
        try {

            const fixUTCString = (dateStr) => {
                if (typeof dateStr === 'string' && dateStr.includes('-') && !dateStr.includes('T')) {
                    return new Date(dateStr + 'T00:00:00');
                }
                return dateStr;
            };

            const vacationData = {
                requestDate: fixUTCString(employee.dCreatedDate),
                employeeName: employee.name || employee.nombreEmpleado,
                startDate: fixUTCString(employee.vacationStart),
                endDate: fixUTCString(employee.vacationEnd),
                totalDays: employee.daysRequested || employee.iDiasSolicitados || 0,
                specificDates: fechasEspecificas,
                estado: employee.estado,
                vPuestoUsuarioAprobador: employee.vPuestoUsuarioAprobador,
                vUsuarioAprobador: employee.vUsuarioAprobador
            };
            console.log("vacationData", vacationData)
            generatePDF(vacationData);


        } catch (error) {
            alert(error.message);
        }
    };

    const handleAprobar = async () => {
        setProcesando(true);
        try {

            const jsonPayload = token ? JSON.parse(atob(token.split('.')[1])) : null;
            const nombreAprobador = jsonPayload?.vNombreCompleto || jsonPayload?.unique_name;

            const response = await fetch('/api/vacaciones/AprobarVacaciones', {
                method: 'POST',
                headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
                body: JSON.stringify({ idSolicitud: employee.idVacacion, vUpdatedUser: nombreAprobador })
            });

            const data = await response.json();
            if (data.iResponseType === 1) {
                // Enviar notificaciones push al solicitante
                if (data.detalles) {
                    try {
                        const notificacionesList = JSON.parse(data.detalles);
                        if (Array.isArray(notificacionesList)) {
                            notificacionesList.forEach((objNotify) => {
                                sendPushNotificationRealTime(objNotify.vToken, objNotify.vCuerpo);
                            });
                        }
                    } catch (e) {
                        console.log("Error parseando notificaciones:", e);
                    }
                }

                if (onSuccess) onSuccess();
                onHide();
            } else {
                alert('Error: ' + data.vMessage);
            }
        } catch (error) {
            alert('Error al aprobar vacaciones');
        } finally {
            setProcesando(false);
        }
    };

    const handleRechazar = () => {
        setShowRejectModal(true);
    };

    const confirmarRechazo = async () => {
        if (!motivoRechazo.trim()) {
            alert('Debe ingresar el motivo del rechazo');
            return;
        }
        setProcesando(true);
        try {
            const response = await fetch('/api/vacaciones/RechazarVacaciones', {
                method: 'POST',
                headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
                body: JSON.stringify({ idSolicitud: employee.idVacacion, motivo: motivoRechazo })
            });
            const data = await response.json();
            if (data.iResponseType === 1) {
                // Enviar notificaciones push al solicitante
                if (data.detalles) {
                    try {
                        const notificacionesList = JSON.parse(data.detalles);
                        if (Array.isArray(notificacionesList)) {
                            notificacionesList.forEach((objNotify) => {
                                sendPushNotificationRealTime(objNotify.vToken, objNotify.vCuerpo);
                            });
                        }
                    } catch (e) {
                        console.log("Error parseando notificaciones:", e);
                    }
                }

                setShowRejectModal(false);
                setMotivoRechazo('');
                if (onSuccess) onSuccess();
                onHide();
            } else {
                alert('Error: ' + data.vMessage);
            }
        } catch (error) {
            alert('Error al rechazar vacaciones');
        } finally {
            setProcesando(false);
        }
    };

    const isPending = employee.estado?.toUpperCase() === 'PENDIENTE';
    const isAdmin = idCatTipoUsuario === 1 || idCatTipoUsuario === '1';
    const shouldShowActionButtons = showActions && isAdmin && isPending;
    const imageToDisplay = getValidAvatar(imagenPerfil || employee.photo);

    return (
        <>
            <Modal show={show} onHide={onHide} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Detalles de Vacaciones</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row>
                        <Col xs={12} className="text-center mb-3">
                            {cargandoImagen ? (
                                <div style={{ width: '100px', height: '100px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Spinner animation="border" variant="primary" />
                                </div>
                            ) : (
                                <img src={imageToDisplay} alt="" style={{ width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #e9ecef', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }} />
                            )}
                        </Col>
                        <Col xs={12}>
                            <div className="text-center mb-3">
                                <h5 className="mb-1 fw-bold">
                                    {employee.idUsuario ? (
                                        <Link to={`/perfil/${employee.idUsuario}`} style={{ textDecoration: 'none', color: 'inherit' }} onClick={onHide}>
                                            {employee.name || employee.nombreEmpleado}
                                        </Link>
                                    ) : (
                                        employee.name || employee.nombreEmpleado
                                    )}
                                </h5>
                                {(employee.position || employee.vNombreTipoUsuario || employee.vPuesto) && (
                                    <p className="text-primary mb-1">{employee.position || employee.vNombreTipoUsuario || employee.vPuesto}</p>
                                )}
                                {getStatusBadge(employee.estado)}
                            </div>
                            <div className="bg-light rounded p-3 border">
                                <Row className="g-2">
                                    <Col xs={12}>
                                        <strong>Categor&iacute;a:</strong>
                                        <span className="ms-2">{renderCategoriaBadge(employee.iCategoria || employee.categoria)}</span>
                                    </Col>

                                    <Col xs={12}>
                                        <div className="mt-2 p-3 bg-white rounded border">
                                            <h6 className="mb-2 fw-bold text-secondary" style={{ fontSize: '0.9rem' }}>
                                                <FontAwesomeIcon icon="comment-alt" className="me-2 text-primary" />
                                                Motivo de la Solicitud
                                            </h6>
                                            {employee.vMotivoSolicitud && employee.vMotivoSolicitud.trim() !== '' ? (
                                                <p className="mb-0 text-dark" style={{ fontStyle: 'italic', fontSize: '0.9rem' }}>
                                                    "{employee.vMotivoSolicitud}"
                                                </p>
                                            ) : (
                                                <p className="mb-0 text-muted small style-italic" style={{ fontStyle: 'italic' }}>No especificado</p>
                                            )}
                                        </div>
                                    </Col>

                                    <Col xs={12}>
                                        <div className="mt-2 p-2 bg-white rounded border">
                                            <h6 className="mb-2">
                                                <FontAwesomeIcon icon="calendar-alt" className="me-2 text-primary" />
                                                Periodo Solicitado
                                            </h6>
                                            <p className="mb-1">
                                                <strong>Inicio:</strong>{' '}{formatDate(employee.vacationStart || employee.dFechaInicio)}
                                            </p>
                                            <p className="mb-1">
                                                <strong>Fin:</strong>{' '}{formatDate(employee.vacationEnd || employee.dFechaFin)}
                                            </p>
                                            <p className="mb-0">
                                                <strong>Total d&iacute;as:</strong>{' '}
                                                <Badge bg="primary" pill>
                                                    {employee.daysRequested || employee.iDiasSolicitados || 0}
                                                </Badge>
                                            </p>
                                        </div>
                                    </Col>

                                    {fechasEspecificas && fechasEspecificas.length > 0 && (
                                        <Col xs={12}>
                                            <div className="mt-2 p-3 bg-white rounded border">
                                                <h6 className="mb-2">
                                                    <FontAwesomeIcon icon="calendar-check" className="me-2 text-success" />
                                                    D&iacute;as Espec&iacute;ficos Solicitados
                                                    <Badge bg="info" pill className="ms-2">{fechasEspecificas.length}</Badge>
                                                </h6>
                                                <div style={{ maxHeight: '200px', overflowY: 'auto', overflowX: 'hidden' }}>
                                                    <ListGroup variant="flush">
                                                        {fechasEspecificas.map((fecha, index) => (
                                                            <ListGroup.Item key={fecha.dateString} className="d-flex justify-content-between align-items-center py-2 px-2" style={{ backgroundColor: 'transparent', border: 'none' }}>
                                                                <div className="d-flex align-items-center">
                                                                    <Badge bg={fecha.isWeekend ? 'secondary' : 'light'} text={fecha.isWeekend ? 'white' : 'dark'} className="me-2" style={{ minWidth: '30px' }}>{index + 1}</Badge>
                                                                    <span className="small text-capitalize">
                                                                        {fecha.formatted}
                                                                        {fecha.isWeekend && <Badge bg="secondary" className="ms-2" style={{ fontSize: '0.65rem' }}>Fin de semana</Badge>}
                                                                    </span>
                                                                </div>
                                                            </ListGroup.Item>
                                                        ))}
                                                    </ListGroup>
                                                </div>
                                            </div>
                                        </Col>
                                    )}

                                    {employee.estado?.toUpperCase() === 'RECHAZADA' && employee.comentarios && (
                                        <Col xs={12}>
                                            <div className="mt-2 p-3 bg-danger bg-opacity-10 rounded border border-danger">
                                                <h6 className="mb-2 text-danger">
                                                    <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
                                                    Motivo del Rechazo
                                                </h6>
                                                <p className="mb-0 text-dark" style={{ whiteSpace: 'pre-wrap' }}>{employee.comentarios}</p>
                                            </div>
                                        </Col>
                                    )}
                                </Row>
                            </div>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-between">
                    <Button variant="outline-danger" onClick={handleGeneratePDF} disabled={isGenerating || procesando}>
                        {isGenerating ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Generando...</> : <><FontAwesomeIcon icon="file-pdf" className="me-2" />Descargar PDF</>}
                    </Button>
                    <div>
                        {shouldShowActionButtons ? (
                            <>
                                <Button variant="secondary" onClick={onHide} disabled={procesando} className="me-2">Cerrar</Button>
                                <Button variant="danger" onClick={handleRechazar} disabled={procesando} className="me-2">
                                    <FontAwesomeIcon icon="times" className="me-2" />Rechazar
                                </Button>
                                <Button variant="success" onClick={handleAprobar} disabled={procesando}>
                                    {procesando ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Procesando...</> : <><FontAwesomeIcon icon="check" className="me-2" />Aprobar</>}
                                </Button>
                            </>
                        ) : (
                            <Button variant="secondary" onClick={onHide} disabled={procesando}>Cerrar</Button>
                        )}
                    </div>
                </Modal.Footer>
            </Modal>

            <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Rechazar Solicitud de Vacaciones</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label>Motivo del rechazo <span className="text-danger">*</span></Form.Label>
                        <Form.Control as="textarea" rows={4} value={motivoRechazo} onChange={(e) => setMotivoRechazo(e.target.value)} placeholder="Ingrese el motivo del rechazo..." disabled={procesando} />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => { setShowRejectModal(false); setMotivoRechazo(''); }} disabled={procesando}>Cancelar</Button>
                    <Button variant="danger" onClick={confirmarRechazo} disabled={procesando || !motivoRechazo.trim()}>
                        {procesando ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Procesando...</> : 'Confirmar Rechazo'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

VacationDetailsModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    employee: PropTypes.object,
    onSuccess: PropTypes.func,
    showActions: PropTypes.bool
};

export default VacationDetailsModal;
